import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Textarea from '../components/ui/Textarea';
import { FiArrowLeft, FiSave, FiPlus, FiX } from 'react-icons/fi';
import { techniques as allTechniques } from '../data/techniques';

const TechniqueEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Find the technique by ID
  const technique = useMemo(() => {
    return allTechniques.find((t) => t.id === parseInt(id));
  }, [id]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: technique
      ? {
          name: technique.name,
          position: technique.position,
          type: technique.type,
          difficulty: technique.difficulty,
          description: technique.description
        }
      : {}
  });

  const [keyPoints, setKeyPoints] = useState(technique?.keyPoints || []);
  const [newKeyPoint, setNewKeyPoint] = useState('');
  const [commonMistakes, setCommonMistakes] = useState(
    technique?.commonMistakes || []
  );
  const [newMistake, setNewMistake] = useState('');

  const positions = [
    'Guard',
    'Mount',
    'Side Control',
    'Back Control',
    'Half Guard',
    'Turtle',
    'Standing',
    'Knee on Belly',
    'Ashi Garami',
    'Front Headlock'
  ];

  const types = [
    'Submission',
    'Sweep',
    'Takedown',
    'Guard Pass',
    'Guard Retention',
    'Escape',
    'Position',
    'Back Take',
    'Control'
  ];

  const difficulties = ['Beginner', 'Intermediate', 'Advanced'];

  if (!technique) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            Technique Not Found
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            The technique you're trying to edit doesn't exist.
          </p>
          <Link to="/techniques">
            <Button>
              <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Techniques
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const addKeyPoint = () => {
    if (newKeyPoint.trim()) {
      setKeyPoints([...keyPoints, newKeyPoint.trim()]);
      setNewKeyPoint('');
    }
  };

  const removeKeyPoint = (index) => {
    setKeyPoints(keyPoints.filter((_, i) => i !== index));
  };

  const addMistake = () => {
    if (newMistake.trim()) {
      setCommonMistakes([...commonMistakes, newMistake.trim()]);
      setNewMistake('');
    }
  };

  const removeMistake = (index) => {
    setCommonMistakes(commonMistakes.filter((_, i) => i !== index));
  };

  const onSubmit = (data) => {
    const updatedTechnique = {
      ...data,
      id: technique.id,
      keyPoints,
      commonMistakes
    };
    console.log('Technique updated:', updatedTechnique);
    // In a real app, you would call an update function here
    alert('Technique updated successfully! (Note: changes are local-only in this demo)');
    navigate(`/techniques/${id}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <Link to={`/techniques/${id}`}>
          <Button variant="ghost" size="sm" className="mb-4">
            <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Technique
          </Button>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Edit Technique
        </h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Update technique details and information.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl dark:text-white">Technique Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">
            {/* Basic Info */}
            <div>
              <Label htmlFor="name" className="text-sm sm:text-base">
                Technique Name
              </Label>
              <Input
                id="name"
                placeholder="e.g., Heel Hook"
                {...register('name', { required: 'Technique name is required' })}
                className="mt-1"
              />
              {errors.name && (
                <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="position" className="text-sm sm:text-base">
                  Position
                </Label>
                <select
                  id="position"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white mt-1"
                  {...register('position', { required: 'Position is required' })}
                >
                  {positions.map((pos) => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
                {errors.position && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.position.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="type" className="text-sm sm:text-base">
                  Type
                </Label>
                <select
                  id="type"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white mt-1"
                  {...register('type', { required: 'Type is required' })}
                >
                  {types.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.type.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="difficulty" className="text-sm sm:text-base">
                  Difficulty
                </Label>
                <select
                  id="difficulty"
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white mt-1"
                  {...register('difficulty', { required: 'Difficulty is required' })}
                >
                  {difficulties.map((diff) => (
                    <option key={diff} value={diff}>
                      {diff}
                    </option>
                  ))}
                </select>
                {errors.difficulty && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.difficulty.message}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-sm sm:text-base">
                Description
              </Label>
              <Textarea
                id="description"
                placeholder="Describe how to perform this technique..."
                rows={4}
                {...register('description', { required: 'Description is required' })}
                className="mt-1"
              />
              {errors.description && (
                <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Key Points */}
            <div>
              <Label className="text-sm sm:text-base font-medium">Key Points</Label>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-3">
                Add important points to remember when executing this technique.
              </p>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter a key point..."
                    value={newKeyPoint}
                    onChange={(e) => setNewKeyPoint(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addKeyPoint();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addKeyPoint}
                    size="md"
                    className="flex-shrink-0"
                  >
                    <FiPlus className="w-4 h-4" />
                  </Button>
                </div>

                {keyPoints.length > 0 && (
                  <div className="space-y-2">
                    {keyPoints.map((point, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <span className="text-sm sm:text-base text-slate-900 dark:text-slate-200 break-words flex-1">
                          {point}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeKeyPoint(index)}
                          className="flex-shrink-0 ml-2"
                        >
                          <FiX className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Common Mistakes */}
            <div>
              <Label className="text-sm sm:text-base font-medium">
                Common Mistakes
              </Label>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-3">
                List common errors to avoid when performing this technique.
              </p>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter a common mistake..."
                    value={newMistake}
                    onChange={(e) => setNewMistake(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addMistake();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addMistake}
                    size="md"
                    className="flex-shrink-0"
                  >
                    <FiPlus className="w-4 h-4" />
                  </Button>
                </div>

                {commonMistakes.length > 0 && (
                  <div className="space-y-2">
                    {commonMistakes.map((mistake, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <span className="text-sm sm:text-base text-slate-900 dark:text-slate-200 break-words flex-1">
                          {mistake}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMistake(index)}
                          className="flex-shrink-0 ml-2"
                        >
                          <FiX className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button type="submit" className="flex-1 w-full sm:w-auto">
                <FiSave className="w-4 h-4 mr-2" /> Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/techniques/${id}`)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TechniqueEdit;